import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { XCircle, Plus, Loader2, DollarSign, Clock, Target, User as UserIcon, CheckCircle2 } from "lucide-react";
import { adminPost, adminGet } from "./utils";
import { AddressAutocomplete } from "../../components/AddressAutocomplete";
import { API } from "@/lib/api";

const TRIP_CLASSES = [
  { value: "local_oneway", label: "Local one-way" },
  { value: "local_roundtrip", label: "Local round trip" },
  { value: "long_distance_oneway", label: "Long distance one-way" },
  { value: "long_distance_roundtrip", label: "Long distance round trip" },
  { value: "discharge", label: "Hospital discharge (on-call)" },
  { value: "dialysis_rt", label: "Dialysis round-trip" },
];

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "cashapp", label: "Cash App" },
  { value: "zelle", label: "Zelle" },
  { value: "check", label: "Check" },
  { value: "other", label: "Other" },
];

export function NewRideModal({ auth, onClose, onCreated }) {
  const [payment, setPayment] = useState("cash_collected"); // "checkout" | "cash_collected" | "insurance" | "waived"
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // Patient lookup — when dispatch types a name and a match exists, offer to fill.
  const [matches, setMatches] = useState([]);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchedPatient, setMatchedPatient] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const [pickup, setPickup] = useState("");
  const [pickupCoords, setPickupCoords] = useState(null);   // {lat, lng} once picked from autocomplete
  const [dropoff, setDropoff] = useState("");
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [tripDate, setTripDate] = useState("");
  const [tripTime, setTripTime] = useState("");
  const [apptTime, setApptTime] = useState("");             // HH:MM — appointment time (destination)
  const [visitMinutes, setVisitMinutes] = useState(60);
  const [returnTrip, setReturnTrip] = useState(false);
  const [pickupWindow, setPickupWindow] = useState(null);   // {recommended_pickup_time, drive_minutes, ...}
  const [pwBusy, setPwBusy] = useState(false);

  // Auto-solve pickup window whenever appt time + both addresses are set
  useEffect(() => {
    if (!apptTime || !tripDate || !pickup.trim() || !dropoff.trim()) {
      setPickupWindow(null); return;
    }
    let cancelled = false;
    const run = async () => {
      setPwBusy(true);
      try {
        const iso = new Date(`${tripDate}T${apptTime}:00`).toISOString();
        const body = {
          pickup: pickup.trim(),
          dropoff: dropoff.trim(),
          appointment_time: iso,
          return_trip: returnTrip,
          visit_minutes: parseInt(visitMinutes, 10) || 60,
        };
        if (pickupCoords?.lat != null) { body.pickup_lat = pickupCoords.lat; body.pickup_lng = pickupCoords.lng; }
        if (dropoffCoords?.lat != null) { body.dropoff_lat = dropoffCoords.lat; body.dropoff_lng = dropoffCoords.lng; }
        const r = await fetch(`${API}/nemt/estimate/pickup-window`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
        const j = await r.json();
        if (!cancelled && r.ok) setPickupWindow(j);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setPwBusy(false); }
    };
    const t = setTimeout(run, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [apptTime, tripDate, pickup, dropoff, pickupCoords, dropoffCoords, returnTrip, visitMinutes]);

  // Debounced patient lookup — search on name/phone as dispatch types.
  useEffect(() => {
    if (matchedPatient) return;   // dispatcher already picked one, don't re-search
    const q = (name.trim().length >= 2 ? name : phone.trim().length >= 4 ? phone : "").trim();
    if (!q) { setMatches([]); setMatchOpen(false); return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await adminGet(auth, `patients?search=${encodeURIComponent(q)}&limit=6`);
        const list = (data.patients || []).concat(data.group_members || []);
        setMatches(list);
        setMatchOpen(list.length > 0);
      } catch (e) { /* silent */ }
      finally { setSearching(false); }
    }, 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [name, phone, matchedPatient, auth]);
  const [tripClass, setTripClass] = useState("local_oneway");
  const [mobility, setMobility] = useState("ambulatory");
  const [waitMinutes, setWaitMinutes] = useState(0);
  const [notes, setNotes] = useState("");

  // Cash-collected
  const [amountDollars, setAmountDollars] = useState("");
  const [method, setMethod] = useState("cash");
  const [payNote, setPayNote] = useState("");

  // Insurance
  const [payerType, setPayerType] = useState("medicaid");
  const [memberId, setMemberId] = useState("");
  const [plan, setPlan] = useState("");
  const [broker, setBroker] = useState("");

  // Waived hold
  const [waiveReason, setWaiveReason] = useState("");
  const [waivedBy, setWaivedBy] = useState("");

  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !pickup.trim() || !dropoff.trim() || !tripDate || !tripTime) {
      toast.error("Name, phone, pickup, dropoff, date, and time are required");
      return;
    }
    if (payment === "cash_collected" && (!amountDollars || parseFloat(amountDollars) < 0)) {
      toast.error("Enter a valid amount collected");
      return;
    }
    if (payment === "waived" && !waiveReason.trim()) {
      toast.error("A reason is required to waive the card hold");
      return;
    }
    setBusy(true);
    try {
      const body = {
        payment_option: payment,
        member_name: name.trim(),
        member_phone: phone.trim(),
        member_email: email.trim() || null,
        patient_id: matchedPatient?.id || null,
        pickup: pickup.trim(),
        dropoff: dropoff.trim(),
        trip_date: tripDate,
        trip_time: tripTime,
        trip_class: tripClass,
        on_call: tripClass === "discharge",
        priority: tripClass === "discharge" ? "high" : "normal",
        mobility,
        wait_minutes: Number(waitMinutes) || 0,
        notes: notes.trim() || null,
        origin_url: window.location.origin,
      };
      if (payment === "cash_collected") {
        body.payment_amount_cents = Math.round(parseFloat(amountDollars || "0") * 100);
        body.payment_method = method;
        body.payment_note = payNote.trim() || null;
      } else if (payment === "insurance") {
        body.payer_type = payerType;
        body.insurance_member_id = memberId.trim() || null;
        body.insurance_plan = plan.trim() || null;
        body.broker = broker.trim() || null;
      } else if (payment === "waived") {
        body.hold_waived_reason = waiveReason.trim();
        body.hold_waived_by = waivedBy.trim() || "dispatcher";
      }
      const res = await adminPost(auth, "rides/create", body);
      if (payment === "checkout" && res.checkout_url) {
        toast.success("Ride created — checkout link ready to share with customer.");
        // Copy to clipboard for convenience
        try {
          await navigator.clipboard.writeText(res.checkout_url);
          toast.info("Checkout URL copied to clipboard");
        } catch { /* ignore */ }
      } else if (payment === "cash_collected") {
        toast.success(`Ride created — $${(res.collected_cents/100).toFixed(2)} ${res.payment_method} recorded`);
      } else if (payment === "waived") {
        toast.success("Ride scheduled — card hold waived (dispatch will collect on completion)");
      } else {
        toast.success("Insurance ride created");
      }
      onCreated?.(res);
      onClose();
    } catch (err) {
      toast.error(err.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6" data-testid="new-ride-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Dispatch booking</div>
            <h3 className="font-serif text-2xl text-brand-deep mt-1">New ride</h3>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-navy"><XCircle className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Payment option */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">How is this being paid?</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { v: "cash_collected", label: "Payment collected", desc: "Cash, Cash App, Zelle — already in hand" },
                { v: "checkout", label: "Send checkout link", desc: "Customer pays online (Stripe hold)" },
                { v: "insurance", label: "Insurance", desc: "Medicaid / Medicare billing" },
                { v: "waived", label: "Waive hold", desc: "VIP / customer satisfaction — dispatch collects later" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setPayment(opt.v)}
                  data-testid={`payment-option-${opt.v}`}
                  className={`text-left px-3 py-2.5 rounded-xl border-2 transition ${payment === opt.v ? "border-brand-green bg-emerald-50" : "border-brand-navy/15 hover:border-brand-navy"}`}
                >
                  <div className="text-sm font-semibold text-brand-deep">{opt.label}</div>
                  <div className="text-[11px] text-brand-muted mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Customer basics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <input value={name} onChange={(e) => { setName(e.target.value); setMatchedPatient(null); }}
                required placeholder="Customer name *" data-testid="nr-name" autoComplete="off"
                onFocus={() => matches.length > 0 && setMatchOpen(true)}
                onBlur={() => setTimeout(() => setMatchOpen(false), 200)}
                className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
              {matchOpen && matches.length > 0 && (
                <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-brand-navy/15 rounded-lg max-h-64 overflow-auto shadow-xl"
                      data-testid="nr-name-matches">
                  <div className="text-[10px] uppercase tracking-[0.14em] px-3 pt-2 pb-1 text-brand-muted font-semibold">
                    Existing patients — click to fill
                  </div>
                  {matches.map((p, i) => (
                    <button key={p.id} type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setName(p.name || "");
                        setPhone(p.phone || "");
                        setEmail(p.email || "");
                        if (p.home_address && !pickup) setPickup(p.home_address);
                        setMatchedPatient(p);
                        setMatchOpen(false);
                        toast.success(`Using ${p.name}'s info`);
                      }}
                      data-testid={`nr-match-${i}`}
                      className="w-full text-left px-3 py-2 hover:bg-brand-surfaceAlt border-b border-brand-navy/5 last:border-b-0">
                      <div className="text-sm font-medium text-brand-deep flex items-center gap-1.5">
                        <UserIcon className="w-3.5 h-3.5 text-brand-green" /> {p.name}
                      </div>
                      <div className="text-[11px] text-brand-muted truncate">
                        {p.phone}{p.email ? ` · ${p.email}` : ""}{p.home_address ? ` · ${p.home_address}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {matchedPatient && (
                <div className="absolute -bottom-5 left-0 text-[10px] text-emerald-700 flex items-center gap-1"
                      data-testid="nr-matched-badge">
                  <CheckCircle2 className="w-3 h-3" /> Using saved patient: {matchedPatient.name}
                </div>
              )}
              {searching && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-muted" />
                </div>
              )}
            </div>
            <input value={phone} onChange={(e) => { setPhone(e.target.value); setMatchedPatient(null); }} required placeholder="Phone *" data-testid="nr-phone" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <input value={email} onChange={(e) => { setEmail(e.target.value); setMatchedPatient(null); }} type="email" placeholder="Email (optional)" data-testid="nr-email" className="sm:col-span-2 px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <AddressAutocomplete
              value={pickup}
              onChange={(v, coords) => { setPickup(v); if (coords) setPickupCoords(coords); else setPickupCoords(null); }}
              placeholder="Pickup address *"
              testid="nr-pickup"
              className="sm:col-span-2 px-4 py-2.5 rounded-lg border border-brand-navy/15 w-full"
            />
            <AddressAutocomplete
              value={dropoff}
              onChange={(v, coords) => { setDropoff(v); if (coords) setDropoffCoords(coords); else setDropoffCoords(null); }}
              placeholder="Dropoff address (facility, clinic, hospital…) *"
              testid="nr-dropoff"
              className="sm:col-span-2 px-4 py-2.5 rounded-lg border border-brand-navy/15 w-full"
            />
            <input value={tripDate} onChange={(e) => setTripDate(e.target.value)} required type="date" data-testid="nr-date" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <input value={tripTime} onChange={(e) => setTripTime(e.target.value)}
              required={tripClass !== "discharge"} type="time"
              data-testid="nr-time" placeholder={tripClass === "discharge" ? "On-call (optional)" : "Pickup time"}
              className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            {tripClass === "discharge" && (
              <div className="sm:col-span-2 bg-rose-50 border-2 border-rose-300 rounded-xl p-3 text-xs text-rose-900" data-testid="discharge-note">
                <strong>Hospital discharge trip</strong> — priority + on-call. Pickup time is optional (dispatch stays on standby until the nurse calls). Ride is flagged <code>on_call=true</code> and rises to the top of the Held tab.
              </div>
            )}
            <div className="sm:col-span-2 bg-blue-50/50 border border-blue-200 rounded-xl p-3 space-y-2" data-testid="appt-panel">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-blue-900 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5" /> Appointment-time back-solver (optional)
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input value={apptTime} onChange={(e) => setApptTime(e.target.value)} type="time"
                  placeholder="Appt time" data-testid="nr-appt-time"
                  className="px-3 py-2 text-sm rounded-lg border border-blue-200 bg-white" />
                <input value={visitMinutes} onChange={(e) => setVisitMinutes(e.target.value)} type="number" min={5} max={480} step={5}
                  placeholder="Visit min" data-testid="nr-visit-minutes"
                  className="px-3 py-2 text-sm rounded-lg border border-blue-200 bg-white" />
                <label className="flex items-center gap-2 text-xs text-blue-900">
                  <input type="checkbox" checked={returnTrip} onChange={(e) => setReturnTrip(e.target.checked)} data-testid="nr-return-trip" />
                  Round trip
                </label>
              </div>
              {pwBusy && <div className="text-xs text-blue-800 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Calculating…</div>}
              {pickupWindow && !pwBusy && (
                <div className="text-xs bg-blue-100 border border-blue-300 rounded-lg p-2 space-y-1" data-testid="pickup-window-banner">
                  <div className="font-semibold text-blue-900 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {pickupWindow.note}
                  </div>
                  {pickupWindow.return_note && (
                    <div className="text-blue-800">↩ {pickupWindow.return_note}</div>
                  )}
                  <button type="button"
                    onClick={() => setTripTime(pickupWindow.recommended_pickup_time.slice(11, 16))}
                    data-testid="use-recommended-time"
                    className="text-xs font-semibold underline hover:no-underline">
                    Use this pickup time
                  </button>
                </div>
              )}
            </div>
            <select value={tripClass} onChange={(e) => setTripClass(e.target.value)} data-testid="nr-class" className="px-4 py-2.5 rounded-lg border border-brand-navy/15">
              {TRIP_CLASSES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={mobility} onChange={(e) => setMobility(e.target.value)} data-testid="nr-mobility" className="px-4 py-2.5 rounded-lg border border-brand-navy/15">
              <option value="ambulatory">Ambulatory</option>
              <option value="wheelchair">Wheelchair</option>
              <option value="stretcher">Stretcher</option>
            </select>
            <div className="sm:col-span-2">
              <label className="text-[11px] text-brand-muted block mb-1">Expected wait at pickup (min) — prevents auto no-show</label>
              <input value={waitMinutes} onChange={(e) => setWaitMinutes(e.target.value)} type="number" min={0} max={240} step={5} placeholder="0" data-testid="nr-wait" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" data-testid="nr-notes" className="sm:col-span-2 px-4 py-2.5 rounded-lg border border-brand-navy/15 resize-none" />
          </div>

          {/* Payment-specific fields */}
          {payment === "cash_collected" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2" data-testid="cash-collected-fields">
              <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Payment collected by dispatch</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-emerald-900/80 block mb-1">Amount ($)</label>
                  <input value={amountDollars} onChange={(e) => setAmountDollars(e.target.value)} type="number" min={0} step="0.01" placeholder="0.00" data-testid="nr-amount" className="w-full px-4 py-2 rounded-lg border border-emerald-300 bg-white" />
                </div>
                <div>
                  <label className="text-[11px] text-emerald-900/80 block mb-1">Method</label>
                  <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="nr-method" className="w-full px-4 py-2 rounded-lg border border-emerald-300 bg-white">
                    {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>
              <input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Receipt / confirmation number (optional)" data-testid="nr-paynote" className="w-full px-4 py-2 rounded-lg border border-emerald-300 bg-white text-sm" />
            </div>
          )}

          {payment === "insurance" && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-2" data-testid="insurance-fields">
              <div className="text-xs font-semibold text-purple-900">Insurance details</div>
              <div className="grid grid-cols-2 gap-2">
                <select value={payerType} onChange={(e) => setPayerType(e.target.value)} data-testid="nr-payer" className="px-4 py-2 rounded-lg border border-purple-300 bg-white">
                  <option value="medicaid">Medicaid</option>
                  <option value="medicare">Medicare</option>
                </select>
                <input value={memberId} onChange={(e) => setMemberId(e.target.value)} placeholder="Member ID" data-testid="nr-memberid" className="px-4 py-2 rounded-lg border border-purple-300 bg-white" />
                <input value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="Plan (e.g. WellCare)" data-testid="nr-plan" className="px-4 py-2 rounded-lg border border-purple-300 bg-white" />
                <input value={broker} onChange={(e) => setBroker(e.target.value)} placeholder="Broker (optional)" data-testid="nr-broker" className="px-4 py-2 rounded-lg border border-purple-300 bg-white" />
              </div>
            </div>
          )}

          {payment === "checkout" && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900" data-testid="checkout-note">
              A Stripe checkout link will be generated. Copy it to the customer via SMS/phone — the ride stays in <strong>pending_payment</strong> until they complete checkout.
            </div>
          )}

          {payment === "waived" && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 space-y-2" data-testid="waived-fields">
              <div className="text-xs font-semibold text-amber-900">
                ⚠️ Owner override — waives the card hold for this ride
              </div>
              <div className="text-[11px] text-amber-800">
                Ride goes straight to <strong>scheduled</strong> — no Stripe hold. Payment method is left unset until dispatch collects on completion. If the customer later adds a card to their wallet, the system will attempt a direct capture of the final fare during the next sweep.
              </div>
              <textarea
                value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)}
                required rows={2}
                placeholder="Reason (required — e.g. 'VIP client · monthly retainer' or 'Service recovery after last-week delay')"
                data-testid="nr-waive-reason"
                className="w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-sm"
              />
              <input
                value={waivedBy} onChange={(e) => setWaivedBy(e.target.value)}
                placeholder="Waived by (your name — optional, defaults to 'dispatcher')"
                data-testid="nr-waived-by"
                className="w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-sm"
              />
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="nr-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create ride
          </button>
        </form>
      </div>
    </div>
  );
}
